/**
 * Meeting Scheduler Component
 * ============================
 * Schedule meetings with leads (Google Meet/Zoom integration)
 */

import React, { useState, useEffect } from 'react';
import { leadAPI, meetingAPI } from '../../api';
import { Card, CardHeader, CardBody, Button, Input, Alert } from '../ui';
import { nowLocalDateTime } from '../../utils/datetime';

const MeetingScheduler = ({ leadId, leadName, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: `Discovery Call - ${leadName}`,
    type: 'discovery',
    scheduled_at: '',
    duration_minutes: 60,
    platform: 'google_meet',
    meeting_link: '',
    attendees: [],
    agenda: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Auto-detect user's timezone
  });
  const [attendeeInput, setAttendeeInput] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [gmStatus, setGmStatus] = useState(null);

  useEffect(() => {
    let active = true;
    meetingAPI
      .getGoogleMeetStatus()
      .then((status) => { if (active) setGmStatus(status); })
      .catch(() => { /* status unavailable — leave gate open, backend still enforces */ });
    return () => { active = false; };
  }, []);

  const isGoogleMeet = formData.platform === 'google_meet';
  const gmReady = gmStatus && gmStatus.connected && gmStatus.enabled;
  const gmBlocked = isGoogleMeet && gmStatus && !gmReady;
  const gmWarning = !gmBlocked
    ? null
    : !gmStatus.connected
      ? 'No Google account is connected for this organization. Connect one under Organization Settings → Meetings & Integrations before scheduling a Google Meet.'
      : 'Google Meet is connected but disabled for this organization. Enable it under Organization Settings → Meetings & Integrations before scheduling a Google Meet.';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Meeting link is only relevant for "other"; clear it when switching away
      if (name === 'platform' && value !== 'other') {
        next.meeting_link = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'platform' && value !== 'other' && errors.meeting_link) {
      setErrors((prev) => ({ ...prev, meeting_link: '' }));
    }
  };

  const handleAddAttendee = () => {
    if (attendeeInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendeeInput)) {
      setFormData((prev) => ({
        ...prev,
        attendees: [...prev.attendees, attendeeInput],
      }));
      setAttendeeInput('');
    }
  };

  const handleRemoveAttendee = (email) => {
    setFormData((prev) => ({
      ...prev,
      attendees: prev.attendees.filter((a) => a !== email),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.scheduled_at) {
      newErrors.scheduled_at = 'Date and time is required';
    } else if (new Date(formData.scheduled_at) <= new Date()) {
      newErrors.scheduled_at = 'Meeting must be scheduled in the future';
    }
    if (formData.platform === 'other' && !formData.meeting_link.trim()) {
      newErrors.meeting_link = 'Meeting link is required for this platform';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Convert local datetime to UTC before sending to backend
      // The datetime-local input gives us local time, but backend expects UTC
      // Example: User selects 12:50 in UTC+5 → converts to 07:50 UTC → sends "2026-02-10T07:50:00Z"
      const localDateTime = new Date(formData.scheduled_at);
      const utcDateTime = localDateTime.toISOString();

      const meetingPayload = {
        ...formData,
        scheduled_at: utcDateTime,
      };

      const response = await leadAPI.scheduleMeeting(leadId, meetingPayload);
      // Backend returns {data: {...}, meta: {...}}
      if (response.data) {
        onSuccess && onSuccess(response.data);
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to schedule meeting' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Schedule Meeting</h3>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}

          {gmWarning && (
            <Alert variant="warning">{gmWarning}</Alert>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Title <span className="text-red-500">*</span>
            </label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Discovery Call"
              error={errors.title}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
              >
                <option value="discovery">Discovery Call</option>
                <option value="follow_up">Follow-up</option>
                <option value="presentation">Presentation</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
              >
                <option value="google_meet">Google Meet</option>
                <option value="other">Other</option>
                <option value="zoom" disabled>Zoom (Coming Soon)</option>
                <option value="teams" disabled>Microsoft Teams (Coming Soon)</option>
              </select>
            </div>
          </div>

          {formData.platform === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Link <span className="text-red-500">*</span>
              </label>
              <Input
                name="meeting_link"
                value={formData.meeting_link}
                onChange={handleChange}
                placeholder="https://..."
                error={errors.meeting_link}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date & Time <span className="text-red-500">*</span>
              </label>
              <Input
                type="datetime-local"
                name="scheduled_at"
                value={formData.scheduled_at}
                onChange={handleChange}
                min={nowLocalDateTime()}
                error={errors.scheduled_at}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes)
              </label>
              <Input
                type="number"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleChange}
                min="15"
                max="480"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Attendees</label>
            <div className="flex gap-2 mb-2">
              <Input
                type="email"
                value={attendeeInput}
                onChange={(e) => setAttendeeInput(e.target.value)}
                placeholder="attendee@email.com"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAttendee();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={handleAddAttendee}>
                Add
              </Button>
            </div>
            {formData.attendees.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.attendees.map((email, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttendee(email)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agenda</label>
            <textarea
              name="agenda"
              value={formData.agenda}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
              placeholder="Meeting agenda and topics to discuss..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading || gmBlocked}>
              {loading ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default MeetingScheduler;

